import React, { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaSlidersH } from 'react-icons/fa';
import { FiCheckCircle, FiTrash2 } from 'react-icons/fi';
import { normalizeMojibake } from '../../../utils/textNormalization';
import { resolveNotificationIcon, formatNotificationDate } from '../../../utils/notificationHelpers';
import { markNotificationsRead, deleteNotification } from '../../../services/notificationsApi';
import './NotificationsSection.css';

const FALLBACK_MESSAGE =
  'Recibiste una actualización importante sobre tus publicaciones o solicitudes.';

export default function NotificationsSection({ notifications = [] }) {
  const navigate = useNavigate();
  const filterPanelRef = useRef(null);
  const [filters, setFilters] = useState({
    type: 'all',
    read: 'all',
    start: '',
    end: '',
  });
  const [acknowledgedIds, setAcknowledgedIds] = useState(() => new Set());
  const [hiddenIds, setHiddenIds] = useState(() => new Set());
  const [isMarkingAll, setIsMarkingAll] = useState(false);
  const [filtersVisible, setFiltersVisible] = useState(false);

  const filteredNotifications = useMemo(() => {
    const source = Array.isArray(notifications) ? notifications : [];
    return source.filter(item => {
      if (!item) return false;
      if (filters.type !== 'all' && item.type !== filters.type) return false;
      if (filters.read === 'read' && !item.read) return false;
      if (filters.read === 'unread' && item.read) return false;
      if (filters.start) {
        const startDate = new Date(filters.start).getTime();
        const created = item.createdAt ? new Date(item.createdAt).getTime() : 0;
        if (created < startDate) return false;
      }
      if (filters.end) {
        const endDate = new Date(filters.end).getTime();
        const created = item.createdAt ? new Date(item.createdAt).getTime() : 0;
        if (created > endDate) return false;
      }
      return true;
    });
  }, [notifications, filters]);

  const orderedNotifications = useMemo(() => {
    return filteredNotifications
      .filter(Boolean)
      .sort((a, b) => {
        const dateA = a?.createdAt ? new Date(a.createdAt).getTime() : 0;
        const dateB = b?.createdAt ? new Date(b.createdAt).getTime() : 0;
        return dateB - dateA;
      });
  }, [filteredNotifications]);

  const visibleNotifications = useMemo(
    () => orderedNotifications.filter(item => item && !hiddenIds.has(item.id)),
    [orderedNotifications, hiddenIds]
  );

  const hydratedNotifications = useMemo(
    () =>
      visibleNotifications.map(item =>
        acknowledgedIds.has(item?.id) ? { ...item, read: true } : item
      ),
    [visibleNotifications, acknowledgedIds]
  );

  const unreadTotal = useMemo(
    () => hydratedNotifications.filter(item => item && !item.read).length,
    [hydratedNotifications]
  );
  const totalNotifications = hydratedNotifications.length;
  const readTotal = totalNotifications - unreadTotal;
  const hasUnread = unreadTotal > 0;

  const handleNavigate = target => {
    if (!target) return;
    navigate(target);
  };

  const handleToggleFilters = () => {
    setFiltersVisible(current => {
      const next = !current;
      if (!current && filterPanelRef.current) {
        filterPanelRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      return next;
    });
  };

  const handleMarkNotificationRead = async item => {
    if (!item?.id) return;
    if (item.read || acknowledgedIds.has(item.id)) return;
    setAcknowledgedIds(prev => {
      const next = new Set(prev);
      next.add(item.id);
      return next;
    });
    try {
      await markNotificationsRead([item.id]);
    } catch (error) {
      console.error('No se pudo marcar la notificación como leída', error);
    }
  };

  const handleMarkAllRead = async () => {
    if (!hasUnread) return;
    const unreadIds = hydratedNotifications
      .filter(item => item && !item.read && item.id)
      .map(item => item.id);
    if (!unreadIds.length) return;
    setIsMarkingAll(true);
    setAcknowledgedIds(prev => {
      const next = new Set(prev);
      unreadIds.forEach(id => next.add(id));
      return next;
    });
    try {
      await markNotificationsRead(unreadIds);
    } catch (error) {
      console.error('No se pudieron marcar las notificaciones como leídas', error);
    } finally {
      setIsMarkingAll(false);
    }
  };

  const handleDeleteNotification = async item => {
    if (!item?.id) return;
    setHiddenIds(prev => {
      const next = new Set(prev);
      next.add(item.id);
      return next;
    });
    try {
      await deleteNotification(item.id);
    } catch (error) {
      console.error('No se pudo eliminar la notificación', error);
    }
  };

  const handleOpenNotification = () => {
    handleNavigate('/dashboard?section=notificaciones');
  };

  const metrics = [
    { label: 'Total registradas', value: totalNotifications },
    { label: 'Sin leer', value: unreadTotal },
    { label: 'Historial', value: readTotal < 0 ? 0 : readTotal },
  ];

  return (
    <section className="notifications-section">
      <header className="notifications-section__hero">
        <div className="notifications-section__intro">
          <span className="notifications-section__kicker">Centro de alertas</span>
          <div className="notifications-section__title-row">
            <h1>Notificaciones</h1>
            <span className="notifications-section__status">
              {unreadTotal > 0 ? `${unreadTotal} nuevas` : 'Todo al día'}
            </span>
          </div>
          <p className="notifications-section__subtitle">
            Monitorea cada actualización sobre tus publicaciones, solicitudes y flujos comerciales.
            Ajusta los filtros para enfocarte en lo que requiere seguimiento inmediato.
          </p>
          <div className="notifications-section__actions">
            <button
              type="button"
              className="notifications-action"
              onClick={handleToggleFilters}
              aria-pressed={filtersVisible}
            >
              <FaSlidersH aria-hidden="true" />
              Filtros avanzados
            </button>
            <button
              type="button"
              className="notifications-action notifications-action--link"
              onClick={handleMarkAllRead}
              disabled={!hasUnread || isMarkingAll}
            >
              Marcar todas como leídas
            </button>
          </div>
        </div>
        <ul className="notifications-section__meta">
          {metrics.map(item => (
            <li key={item.label}>
              <span className="notifications-section__meta-label">{item.label}</span>
              <strong className="notifications-section__meta-value">{item.value}</strong>
            </li>
          ))}
        </ul>
      </header>
      <div className="notifications-layout">
        <div className="notifications-feed">
          {!hydratedNotifications.length ? (
            <div className="notifications-empty">
              <p>No encontramos notificaciones con los filtros actuales.</p>
              <p>Intenta ajustar la búsqueda o elimina los filtros.</p>
            </div>
          ) : (
            <ul className="notifications-feed-list">
              {hydratedNotifications.map(item => {
                const NotificationIcon = resolveNotificationIcon(item?.type);
                const title = normalizeMojibake(item?.title) || 'Nueva notificación';
                const message =
                  normalizeMojibake(item?.message) ||
                  normalizeMojibake(item?.data?.message) ||
                  FALLBACK_MESSAGE;
                const formattedDate = formatNotificationDate(item?.createdAt);
                const isUnread = !item?.read;
                return (
                  <li key={item?.id || `${item?.type}-${item?.createdAt}`}>
                    <div
                      className={`notifications-row${isUnread ? ' is-unread' : ''}`}
                    >
                      <button
                        type="button"
                        className="notifications-row__main"
                        onClick={handleOpenNotification}
                      >
                        <span className="notifications-row__icon" aria-hidden="true">
                          <NotificationIcon />
                        </span>
                        <span className="notifications-row__body">
                          <span className="notifications-row__title">
                            {isUnread ? (
                              <span className="notifications-row__dot" aria-hidden="true" />
                            ) : null}
                            <span>{title}</span>
                          </span>
                          <span className="notifications-row__message">{message}</span>
                        </span>
                      </button>
                      <div className="notifications-row__aside">
                        {formattedDate ? (
                          <time className="notifications-row__date">{formattedDate}</time>
                        ) : null}
                        <div className="notifications-row__actions">
                          <button
                            type="button"
                            className="notifications-row__action"
                            onClick={() => handleMarkNotificationRead(item)}
                            disabled={!isUnread}
                            aria-label="Marcar como leída"
                          >
                            <FiCheckCircle aria-hidden="true" />
                          </button>
                          <button
                            type="button"
                            className="notifications-row__action notifications-row__action--danger"
                            onClick={() => handleDeleteNotification(item)}
                            aria-label="Eliminar notificación"
                          >
                            <FiTrash2 aria-hidden="true" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
        <aside
          ref={filterPanelRef}
          className={`notifications-filter card${filtersVisible ? ' is-open' : ''}`}
          id="notifications-filter-panel"
        >
          <div className="notifications-filter__header">
            <FaSlidersH aria-hidden="true" />
            <div>
              <p>Filtros</p>
              <small>Refina por fecha y tipo</small>
            </div>
          </div>
          <label className="notifications-filter__field">
            <span>Tipo</span>
            <select
              value={filters.type}
              onChange={event =>
                setFilters(current => ({ ...current, type: event.target.value }))
              }
            >
              <option value="all">Todos</option>
              <option value="reservation">Reservas</option>
              <option value="request_proposal">Propuestas</option>
              <option value="publication_view">Visitas a publicación</option>
              <option value="profile_view">Visitas al perfil</option>
            </select>
          </label>
          <label className="notifications-filter__field">
            <span>Estado</span>
            <select
              value={filters.read}
              onChange={event =>
                setFilters(current => ({ ...current, read: event.target.value }))
              }
            >
              <option value="all">Todas</option>
              <option value="unread">Sólo nuevas</option>
              <option value="read">Sólo leídas</option>
            </select>
          </label>
          <div className="notifications-filter__row">
            <label className="notifications-filter__field">
              <span>Desde</span>
              <input
                type="date"
                value={filters.start}
                onChange={event =>
                  setFilters(current => ({ ...current, start: event.target.value }))
                }
              />
            </label>
            <label className="notifications-filter__field">
              <span>Hasta</span>
              <input
                type="date"
                value={filters.end}
                onChange={event =>
                  setFilters(current => ({ ...current, end: event.target.value }))
                }
              />
            </label>
          </div>
          <button
            type="button"
            className="notifications-filter__reset"
            onClick={() => setFilters({ type: 'all', read: 'all', start: '', end: '' })}
          >
            Reiniciar filtros
          </button>
        </aside>
      </div>
    </section>
  );
}
